import React, { useState } from "react";
import SEOHead from "../../components/SEO/SEOHead";
import { generateWebPageStructuredData, generateBreadcrumbStructuredData } from "../../components/SEO/StructuredData";
import LegalPageLayout from "../../components/Legal/LegalPageLayout";

const DataProcessingAgreement = () => {
  const [activeSection, setActiveSection] = useState("purpose");

  const currentDate = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const structuredData = generateWebPageStructuredData({
    name: "Data Processing Agreement",
    description: "Data Processing Agreement for tournament organizers. Learn about data controller and processor responsibilities, permitted uses, security requirements, and liability.",
    url: "/data-processing-agreement",
    dateModified: new Date().toISOString(),
  });

  const breadcrumbData = generateBreadcrumbStructuredData([
    { label: "Home", path: "/" },
    { label: "Data Processing Agreement", path: "/data-processing-agreement" },
  ]);

  const sections = [
    { id: "purpose", title: "Purpose" },
    { id: "roles", title: "Roles" },
    { id: "categories", title: "Categories of Data" },
    { id: "controller-responsibilities", title: "Controller Responsibilities" },
    { id: "processor-responsibilities", title: "Processor Responsibilities" },
    { id: "data-sharing", title: "Data Sharing" },
    { id: "data-retention", title: "Data Retention" },
    { id: "security", title: "Security Measures" },
    { id: "breach", title: "Breach Notification" },
    { id: "liability", title: "Liability" },
    { id: "representative", title: "Representative Authority" },
    { id: "amendments", title: "Amendments and Updates" },
    { id: "governing-law", title: "Governing Law & Arbitration" },
    { id: "acceptance", title: "Acceptance" },
  ];

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <>
      <SEOHead
        title="Data Processing Agreement - Bowling Tournament App"
        description="Data Processing Agreement for tournament organizers. Learn about data controller and processor responsibilities, permitted uses, security requirements, and liability."
        keywords={[
          "data processing agreement",
          "DPA",
          "data controller",
          "data processor",
          "GDPR",
          "privacy compliance",
          "tournament organizer",
          "data sharing",
        ]}
        canonicalUrl="/data-processing-agreement"
        noIndex={false}
        additionalMeta={[
          { name: "last-modified", content: currentDate },
          { name: "author", content: "Bowling Tournament App" },
        ]}
        structuredData={structuredData}
        additionalStructuredData={[breadcrumbData]}
      />
      <LegalPageLayout
        title="Data Processing Agreement (DPA)"
        sections={sections}
        activeSection={activeSection}
        onSectionClick={scrollToSection}
        headerExtra={
          <>
            <p className="text-brand-muted mb-2">
              <strong className="text-brand-charcoal">Between:</strong> Bowling Tournament App
              ("Processor")
            </p>
            <p className="text-brand-muted mb-2">
              <strong className="text-brand-charcoal">and</strong>
            </p>
            <p className="text-brand-muted mb-4">
              <strong className="text-brand-charcoal">Organization / Group / Team</strong> ("Controller")
            </p>
            <p className="text-brand-muted mb-4">Effective Date: {currentDate}</p>
            <div className="card-inset border-l-4 border-brand-charcoal p-4 my-6">
              <p className="text-brand-charcoal text-sm">
                <strong>Important:</strong> This is a legal agreement. Viewing
                this document does not create a binding contract. A binding
                agreement is formed only when an authorized representative of
                your organization accepts this DPA through the platform's
                acceptance mechanism (see Section 14). This document is
                provided for informational purposes and to facilitate the
                acceptance process.
              </p>
            </div>
          </>
        }
      >
        <div className="space-y-12">
                {/* Purpose Section */}
                <section id="purpose">
                  <h2 className="heading-display text-xl mb-4">
                    1. Purpose
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <p>
                      This Agreement governs how the Processor handles personal
                      information submitted by the Controller (organization,
                      team, or group) for administering bowling tournaments,
                      including participant registration, roster management, and
                      communications.
                    </p>
                  </div>
                </section>

                {/* Roles Section */}
                <section id="roles">
                  <h2 className="heading-display text-xl mb-4">
                    2. Roles
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        <strong>Controller (Organization/Group):</strong>{" "}
                        determines what personal data is collected and how it is
                        used.
                      </li>
                      <li>
                        <strong>Processor (Platform):</strong> stores,
                        processes, and transmits personal data{" "}
                        <strong>only</strong> as instructed by the Controller.
                      </li>
                    </ul>
                  </div>
                </section>

                {/* Categories of Data Section */}
                <section id="categories">
                  <h2 className="heading-display text-xl mb-4">
                    3. Categories of Data
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <p className="mb-4">
                      Data may include, but is not limited to:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mb-6">
                      <li>Bowler name</li>
                      <li>Email address</li>
                      <li>Phone number</li>
                      <li>USBC ID</li>
                      <li>Average</li>
                      <li>Team/squad info</li>
                      <li>
                        Any additional information entered into the platform
                      </li>
                    </ul>
                    <div className="card-inset border-l-4 border-brand-charcoal p-4 my-6">
                      <p className="text-brand-charcoal">
                        <strong>Note:</strong> For California residents,
                        "Personal Information" is defined under the California
                        Consumer Privacy Act (CCPA/CPRA) as any information that
                        identifies, relates to, describes, or could reasonably
                        be linked, directly or indirectly, to an individual.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Controller Responsibilities Section */}
                <section id="controller-responsibilities">
                  <h2 className="heading-display text-xl mb-4">
                    4. Controller Responsibilities
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <p className="mb-4">
                      The Controller confirms that:
                    </p>
                    <ol className="list-decimal pl-6 space-y-2">
                      <li>
                        They are authorized to act on behalf of the
                        organization/group they represent.
                      </li>
                      <li>
                        They have permission to enter all personal data submitted
                        through the platform.
                      </li>
                      <li>
                        They are responsible for accuracy, legality, and
                        authorization of all submitted data.
                      </li>
                      <li>
                        Data is used solely for tournament administration.
                      </li>
                      <li>
                        They will comply with applicable data protection laws,
                        including CCPA/CPRA for California residents and GDPR
                        where applicable.
                      </li>
                      <li>
                        They will honor any deletion or access requests as
                        required by law.
                      </li>
                      <li>
                        They acknowledge that the Processor retains personal data
                        according to the platform's{" "}
                        <a
                          href="/data-retention-policy"
                          className="text-brand-gold-ink hover:text-brand-charcoal underline"
                        >
                          Data Retention Policy
                        </a>
                        .
                      </li>
                    </ol>
                  </div>
                </section>

                {/* Processor Responsibilities Section */}
                <section id="processor-responsibilities">
                  <h2 className="heading-display text-xl mb-4">
                    5. Processor Responsibilities
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <p className="mb-4">The Processor shall:</p>
                    <ol className="list-decimal pl-6 space-y-2">
                      <li>
                        Process personal data solely for tournament
                        administration.
                      </li>
                      <li>
                        Implement reasonable technical and administrative
                        safeguards.
                      </li>
                      <li>Not sell or misuse personal data.</li>
                      <li>
                        Restrict access to employees and contractors bound by
                        confidentiality.
                      </li>
                      <li>
                        Assist the Controller with deletion or access requests
                        where legally permissible.
                      </li>
                      <li>
                        Retain data in accordance with the{" "}
                        <a
                          href="/data-retention-policy"
                          className="text-brand-gold-ink hover:text-brand-charcoal underline"
                        >
                          Data Retention Policy
                        </a>
                        , which may be updated over time.
                      </li>
                    </ol>
                  </div>
                </section>

                {/* Data Sharing Section */}
                <section id="data-sharing">
                  <h2 className="heading-display text-xl mb-4">
                    6. Data Sharing
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <p className="mb-4">
                      The Processor may share personal data only with:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        Tournament organizers responsible for the event
                      </li>
                      <li>
                        Service providers required to operate the platform (e.g.,
                        email delivery systems)
                      </li>
                      <li>
                        Stripe, when the organization turns on Card payments.
                        The organization is the merchant of record. Stripe
                        processes the card charge.
                      </li>
                      <li>Law enforcement if required by law</li>
                    </ul>
                  </div>
                </section>

                {/* Data Retention Section */}
                <section id="data-retention">
                  <h2 className="heading-display text-xl mb-4">
                    7. Data Retention
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        Personal data is retained according to the{" "}
                        <a
                          href="/data-retention-policy"
                          className="text-brand-gold-ink hover:text-brand-charcoal underline"
                        >
                          Data Retention Policy
                        </a>
                        .
                      </li>
                      <li>
                        After the retention period, personal identifiers may be
                        anonymized for historical/statistical purposes.
                      </li>
                      <li>
                        Legal or regulatory obligations may override deletion or
                        anonymization.
                      </li>
                    </ul>
                  </div>
                </section>

                {/* Security Measures Section */}
                <section id="security">
                  <h2 className="heading-display text-xl mb-4">
                    8. Security Measures
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <p className="mb-4">
                      The Processor will maintain reasonable security measures,
                      including:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Access controls</li>
                      <li>Authentication & logging</li>
                      <li>Encrypted transmission</li>
                      <li>Regular vulnerability monitoring</li>
                      <li>Secure backups</li>
                    </ul>
                  </div>
                </section>

                {/* Breach Notification Section */}
                <section id="breach">
                  <h2 className="heading-display text-xl mb-4">
                    9. Breach Notification
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        Both parties must notify each other without unreasonable
                        delay of any data breach affecting personal information.
                      </li>
                      <li>
                        Parties will cooperate to assess and remediate the
                        breach.
                      </li>
                    </ul>
                  </div>
                </section>

                {/* Liability Section */}
                <section id="liability">
                  <h2 className="heading-display text-xl mb-4">
                    10. Liability
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        Controller is responsible for data entered without proper
                        authorization.
                      </li>
                      <li>
                        Processor is responsible only for platform security and
                        processing.
                      </li>
                    </ul>
                  </div>
                </section>

                {/* Representative Authority Section */}
                <section id="representative">
                  <h2 className="heading-display text-xl mb-4">
                    11. Representative Authority Attestation
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <div className="card-inset border-l-4 border-brand-charcoal p-4 my-6">
                      <p className="text-brand-charcoal">
                        "By accepting this agreement, I confirm that I am
                        authorized to act on behalf of the organization, team,
                        or group I manage, and that I and the organization/group
                        are responsible for the personal data entered into the
                        system."
                      </p>
                    </div>
                  </div>
                </section>

                {/* Amendments Section */}
                <section id="amendments">
                  <h2 className="heading-display text-xl mb-4">
                    12. Amendments and Updates
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <p className="mb-4">
                      The Processor reserves the right to modify or update this
                      Agreement at any time.
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        Updated versions will be published on the platform.
                      </li>
                      <li>
                        At least one organization owner or admin must accept the
                        updated version before the organization can continue using
                        features that process personal data.
                      </li>
                      <li>
                        Continued use of the platform after notification by an
                        organization owner or admin constitutes acceptance on behalf
                        of the organization.
                      </li>
                    </ul>
                  </div>
                </section>

                {/* Governing Law Section */}
                <section id="governing-law">
                  <h2 className="heading-display text-xl mb-4">
                    13. Governing Law & Arbitration
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        This Agreement is governed by the laws of the State of
                        California.
                      </li>
                      <li>
                        Any dispute, claim, or controversy arising out of or
                        relating to this Agreement or the Platform shall be
                        resolved exclusively by binding arbitration under the
                        rules described in the{" "}
                        <a
                          href="/terms-of-service#dispute-resolution"
                          className="text-brand-gold-ink hover:text-brand-charcoal underline"
                        >
                          Terms of Service
                        </a>
                        , including any waivers of class-action rights.
                      </li>
                      <li>
                        By accepting this Agreement, the Controller acknowledges
                        that they have read and agreed to the arbitration
                        provisions in the Terms of Service.
                      </li>
                    </ul>
                  </div>
                </section>

                {/* Acceptance Section */}
                <section id="acceptance">
                  <h2 className="heading-display text-xl mb-4">
                    14. Acceptance
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <p className="mb-4">
                      This Agreement becomes binding only upon acceptance by an
                      authorized representative of the Controller. Acceptance
                      occurs when an authorized user of the organization
                      (typically an organization owner) accepts this Agreement
                      through the platform's acceptance mechanism.
                    </p>
                    <p className="mb-4">
                      The acceptance mechanism requires the user to affirm the
                      following statement:
                    </p>
                    <div className="card-inset border-l-4 border-brand-charcoal p-4 my-6">
                      <p className="text-brand-charcoal">
                        "I confirm I am authorized to act on behalf of this
                        organization/group and agree to the Data Processing
                        Agreement and the Data Retention Policy."
                      </p>
                    </div>
                    <p className="mb-4">
                      Acceptance is recorded and maintained for legal audit
                      purposes. This acceptance creates a binding legal agreement
                      between the Controller (organization) and the Processor
                      (Bowling Tournament App) as of the acceptance timestamp.
                    </p>
                  </div>
                </section>

                {/* Contact & Updates Section */}
                <section className="border-t border-brand-charcoal/10 pt-8">
                  <h2 className="heading-display text-xl mb-4">
                    Contact & Updates
                  </h2>
                  <div className="max-w-none text-brand-charcoal leading-relaxed">
                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3">
                      Questions About This Agreement?
                    </h3>
                    <p className="mb-4">
                      If you have questions about this Data Processing Agreement,
                      please contact us:
                    </p>
                    <p className="font-medium">
                      Email: privacy@bowlingtournamentapp.com
                    </p>
                    <p className="text-sm text-brand-muted">
                      We typically respond within 24-48 hours
                    </p>

                    <h3 className="font-display font-bold text-brand-charcoal text-lg mb-3 mt-8">
                      Agreement Updates
                    </h3>
                    <p>
                      We may update this Agreement from time to time to reflect
                      changes in our services or legal requirements.
                    </p>
                    <p className="font-medium mt-4">
                      Last Updated: {currentDate}
                    </p>
                    <p className="text-sm text-brand-muted">
                      Material changes to this Agreement will be communicated via email to
                      organization owners and administrators. By continuing to use the platform
                      after receiving notice of material changes, you agree to be bound by the
                      updated Agreement. If you do not agree to the changes, you may discontinue
                      use of the platform.
                    </p>
                  </div>
                </section>
        </div>
      </LegalPageLayout>
    </>
  );
};

export default DataProcessingAgreement;
